
import { useState, useCallback, useEffect } from 'react';
import type { CalculatorInputs, CalculatorResults } from '../types';

const initialResults: CalculatorResults = {
  contentForDigitization: 0,
  totalDigitalCourseDuration: 0,
  videoContentDuration: 0,
  interactiveContentDuration: 0,
  numberOfVideos: 0,
  numberOfReadingMaterials: 0,
  numberOfInteractiveActivities: 0,
  totalCost: 0,
  interactiveShare: 0,
};

export const useBlendedLearningCalculator = (initialInputs: CalculatorInputs) => {
  const [inputs, setInputs] = useState<CalculatorInputs>(initialInputs);
  const [results, setResults] = useState<CalculatorResults>(initialResults);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const calculate = async () => {
      setLoading(true);
      try {
        const response = await fetch('/api/calculate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(inputs),
          signal,
        });

        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        
        const data: CalculatorResults = await response.json();
        setResults(data);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch calculation:", error);
          // Here you could set an error state to display in the UI
        }
      } finally {
        setLoading(false);
      }
    };
    
    // Debounce the calculation to avoid excessive API calls
    const timerId = setTimeout(() => {
        calculate();
    }, 300);


    return () => {
      clearTimeout(timerId);
      controller.abort();
    };
  }, [inputs]);

  const handleInputChange = useCallback((field: keyof CalculatorInputs, value: number) => {
    // Prevent negative numbers where they don't make sense
    const sanitizedValue = Math.max(0, value);
    setInputs(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  }, []);

  return { inputs, results, loading, handleInputChange };
};
